import React, { useEffect, useRef, useState } from 'react';
import { useSiteSettings } from '../contexts/SiteSettingsContext';
import { useTranslation } from 'react-i18next';
import { formatCategoryTreeLabel } from '../utils/categoryI18n';
import { DynamicCategoryIcon } from './DynamicCategoryIcon';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { CategoryNode } from '../types';
import { SlidersHorizontal, RotateCcw } from 'lucide-react';

interface CategoryIconBarProps {
  selectedCategory: string;
  onCategoryChange: (id: string) => void;
  onOpenFilter: () => void;
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
}

const AUTO_SCROLL_PX_PER_SEC = 30;
const RESUME_DELAY_MS = 3000;
/** فرق يتجاوز هذا الحد يعني أن المستخدم هو من مرّر، لا حلقة الحركة */
const SELF_SCROLL_TOLERANCE_PX = 2;

const CategoryIconBar: React.FC<CategoryIconBarProps> = ({ 
  selectedCategory, 
  onCategoryChange,
  onOpenFilter,
  hasActiveFilters,
  onClearFilters
}) => {
  const { categories } = useSiteSettings();
  const { t, i18n } = useTranslation('common');

  const scrollerRef = useRef<HTMLDivElement>(null);
  const isSmallScreen = useMediaQuery('(max-width: 1023px)');
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [overflows, setOverflows] = useState(false);
  const autoScroll = isSmallScreen && !reduceMotion && overflows && categories.length > 0;

  // القياس على مجموعة واحدة حتى لا يقلب التكرار النتيجة
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const measure = () => {
      const contentWidth = autoScroll ? el.scrollWidth / 2 : el.scrollWidth;
      setOverflows(contentWidth > el.clientWidth + 8);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [autoScroll, categories.length, i18n.language]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || !autoScroll) return;

    const dirSign = getComputedStyle(el).direction === 'rtl' ? -1 : 1;
    let frameId = 0;
    let resumeTimer: number | undefined;
    let paused = false;
    let lastTs = 0;
    let carry = 0;
    let expectedLeft: number | null = null;

    const pause = () => {
      paused = true;
      if (resumeTimer) window.clearTimeout(resumeTimer);
      resumeTimer = window.setTimeout(() => {
        paused = false;
        lastTs = 0;
      }, RESUME_DELAY_MS);
    };

    const step = (ts: number) => {
      frameId = requestAnimationFrame(step);

      if (paused || document.hidden) {
        lastTs = 0;
        return;
      }
      if (!lastTs) {
        lastTs = ts;
        return;
      }

      // سقف الفارق يمنع قفزة كبيرة بعد رجوع التبويب
      carry += (AUTO_SCROLL_PX_PER_SEC * Math.min(ts - lastTs, 100)) / 1000;
      lastTs = ts;

      const whole = Math.floor(carry);
      if (whole < 1) return;
      carry -= whole;

      const half = el.scrollWidth / 2;
      if (half > 0 && Math.abs(el.scrollLeft) >= half) {
        el.scrollLeft -= dirSign * half;
      }
      el.scrollLeft += dirSign * whole;
      expectedLeft = el.scrollLeft;
    };

    const onScroll = () => {
      if (expectedLeft !== null && Math.abs(el.scrollLeft - expectedLeft) <= SELF_SCROLL_TOLERANCE_PX) return;
      pause();
    };

    const pauseEvents = ['pointerdown', 'touchstart', 'touchend', 'wheel', 'mouseenter', 'focusin'] as const;
    pauseEvents.forEach((name) => el.addEventListener(name, pause, { passive: true }));
    el.addEventListener('scroll', onScroll, { passive: true });
    frameId = requestAnimationFrame(step);

    return () => {
      cancelAnimationFrame(frameId);
      if (resumeTimer) window.clearTimeout(resumeTimer);
      pauseEvents.forEach((name) => el.removeEventListener(name, pause));
      el.removeEventListener('scroll', onScroll);
    };
  }, [autoScroll]);

  const renderCategory = (cat: CategoryNode, isDuplicate = false) => {
    const isActive = selectedCategory === cat.id;

    return (
      <button
        key={isDuplicate ? `dup-${cat.id}` : cat.id}
        type="button"
        onClick={() => onCategoryChange(cat.id)}
        aria-hidden={isDuplicate || undefined}
        tabIndex={isDuplicate ? -1 : undefined}
        className="flex flex-col items-center gap-0.5 min-w-[42px] sm:min-w-[64px] transition-all duration-300 group"
      >
        <div className={`relative flex h-7 w-7 items-center justify-center rounded-lg sm:h-12 sm:w-12 sm:rounded-2xl transition-all duration-300 ${
          isActive 
            ? 'bg-primary-500 text-white shadow-md shadow-primary-500/30 scale-105 sm:scale-110' 
            : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 group-hover:bg-slate-200 dark:group-hover:bg-slate-700'
        }`}>
          <DynamicCategoryIcon category={cat} className="w-4 h-4 sm:w-6 sm:h-6" isActive={isActive} />
          {isActive && (
            <span className="absolute -bottom-1 w-1 h-1 bg-white rounded-full animate-pulse sm:w-1.5 sm:h-1.5" />
          )}
        </div>
        <span className={`text-[8px] sm:text-[10px] font-bold whitespace-nowrap transition-colors max-w-[4rem] truncate sm:max-w-none ${
          isActive ? 'text-primary-600 dark:text-primary-400' : 'text-slate-500 dark:text-slate-400'
        }`}>
          {formatCategoryTreeLabel(cat, i18n.language, t)}
        </span>
      </button>
    );
  };

  return (
    <div className="w-full overflow-hidden">
      <div className="flex items-center gap-1 px-1 py-0.5 sm:gap-2 sm:px-4 sm:py-2">
        {/* زر الفلاتر الجانبي */}
        <div className="shrink-0 border-e border-slate-200 pe-1 dark:border-slate-700 sm:pe-2 flex gap-1">
          <button
            type="button"
            onClick={onOpenFilter}
            className="flex flex-col items-center gap-0.5 text-slate-600 transition-colors hover:text-primary-500 dark:text-slate-400 sm:gap-1"
          >
            <div className="relative flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 bg-white shadow-sm transition-all hover:shadow-md dark:border-slate-700 dark:bg-slate-800 sm:h-11 sm:w-11 sm:rounded-2xl">
              <SlidersHorizontal className="h-3.5 w-3.5 sm:h-5 sm:w-5" />
              {hasActiveFilters && (
                <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5 items-center justify-center rounded-full bg-red-500 ring-2 ring-white dark:ring-slate-900 sm:h-3 sm:w-3"></span>
              )}
            </div>
            <span className="max-w-[3.25rem] truncate text-[7.5px] font-bold leading-none sm:max-w-none sm:text-[9px]">
              {t('home.filters.title')}
            </span>
          </button>

          {hasActiveFilters && onClearFilters && (
            <button
              type="button"
              onClick={onClearFilters}
              className="flex flex-col items-center gap-0.5 text-red-500 transition-colors hover:text-red-600 sm:gap-1"
            >
              <div className="flex h-7 w-7 items-center justify-center rounded-lg border border-red-200 bg-red-50 shadow-sm transition-all hover:shadow-md dark:border-red-900/30 dark:bg-red-900/20 sm:h-11 sm:w-11 sm:rounded-2xl">
                <RotateCcw className="h-3.5 w-3.5 sm:h-5 sm:w-5" />
              </div>
              <span className="max-w-[3.25rem] truncate text-[7.5px] font-bold leading-none sm:max-w-none sm:text-[9px]">
                {t('home.filters.clear', 'إلغاء')}
              </span>
            </button>
          )}
        </div>

        {/* Horizontal Scroll Area */}
        <div
          ref={scrollerRef}
          className="min-w-0 flex-1 overflow-x-auto no-scrollbar flex items-center gap-1.5 scroll-auto sm:gap-5"
        >
          {categories.map((cat) => renderCategory(cat))}
          {/* نسخة ثانية تجعل الالتفاف بلا قطع، مثل الشريط الدعائي */}
          {autoScroll && categories.map((cat) => renderCategory(cat, true))}
        </div>
      </div>
    </div>
  );
};

export default CategoryIconBar;
